import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';

import './Login.css';
import NavBar from '../NavBar';
import Api from '../../lib/Api';
import Storage from '../../lib/storage';
import { useAppDispatch, useAppSelector } from '../../lib/hooks';
import { updateChannel, updateSessionToken } from '../../lib/slices/imparcialApp';


const Login: React.FC = () => {

    const sessionToken = useAppSelector((state) => state.imparcial.sessionToken);

    const [email, setEmail] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [confirmationCode, setConfirmationCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [codeId, setCodeId] = useState('');
    const [resendCode, setResendCode] = useState(false);
    const [error, setError] = useState('');
    const [accountConfirmed, setAccountConfirmed] = useState(false);
    const [showCodeResendConfirmation, setShowCodeResendConfirmation] = useState(false);
    const [didConfirmAccountDuringLogin, setDidConfirmAccountDuringLogin] = useState(false);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();


    useEffect(() => {
        if(sessionToken != "") {
            navigate("/", { replace: true });
        }
    }, []);


    const validateEmail = (email: string) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (email == "") {
            setError('Entre un Correo Electrónico.');
            return;
        }

        if (!validateEmail(email)) {
            setError('El Correo Electrónico no es válido.');
            return;
        }

        setIsLoading(true);
        const loginApiResponse = await Api.login(email);
        if(!loginApiResponse.Error) {
            const loginResponse = loginApiResponse.Data;
            if(loginResponse) {
                setCodeId(loginResponse.CodeId);
                setAccountConfirmed(loginResponse.IsConfirmed);
                setIsSubmitted(true);       
            }
        }
        else {
            setError('Hubo un error iniciando sesión. Verifique sus credenciales.');
        }
        setIsLoading(false);
    };

    const handleVerification = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const code = Number.parseInt(confirmationCode);
        if (!code) {
            setError('El código suministrado es invalido. Entre un código válido.');
            return;
        }

        setIsLoading(true);
        const verifyLoginCodeResult = await Api.verifyLoginCode(codeId, code);
        setIsLoading(false);
        setConfirmationCode('');
        if(!verifyLoginCodeResult.Error) {
            if(verifyLoginCodeResult.Data) {
                Storage.setSessionToken(verifyLoginCodeResult.Data.SessionToken);
                dispatch(updateSessionToken(verifyLoginCodeResult.Data.SessionToken));
                dispatch(updateChannel(verifyLoginCodeResult.Data.Channel));
                navigate("/", { replace: true });
            }
            else {
                setError('El código suministrado es invalido. Entre un código válido.');
            }
        }
        else {
            setError('El código suministrado es invalido. Entre un código válido.');
        }
    };

    const handleConfirm = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const code = Number.parseInt(confirmationCode);
        if (!code) {
            setError('El código suministrado es invalido. Entre un código válido.');
            return;
        }

        setIsLoading(true);
        const confirmAccountResponse = await Api.confirmAccount(codeId, code);
        if(!confirmAccountResponse.Error) {
            // confirmed correctly
            if(confirmAccountResponse.Data) {
                const loginApiResponse = await Api.login(email);
                if(!loginApiResponse.Error) {
                    const loginResponse = loginApiResponse.Data;
                    if(loginResponse) {
                        setCodeId(loginResponse.CodeId);
                        setAccountConfirmed(loginResponse.IsConfirmed);
                        setDidConfirmAccountDuringLogin(true);
                    }
                    else {
                        setError('Hubo un error iniciando sesión. Verifique sus credenciales.');
                    }
                }
            }
            else {
                setError('El código suministrado es invalido. Entre un código válido.');
            }
        }
        else {
            setError('El código suministrado es invalido. Entre un código válido.');
        }
        
        setConfirmationCode('');
        setIsLoading(false);
    };

    const resendVerificationCode = async (e: React.FormEvent) => {
        e.preventDefault();

        setResendCode(true);
        const resendLoginCodeResponse = await Api.resendLoginVerificationCode(email);
        if(!resendLoginCodeResponse.Error && resendLoginCodeResponse.Data)
            setCodeId(resendLoginCodeResponse.Data);
        setConfirmationCode('');
        setResendCode(false);
        setShowCodeResendConfirmation(true);
        setTimeout(() => {
            setShowCodeResendConfirmation(false);
        }, 5000);

    };

    const resendConfirmationCode = async (e: React.FormEvent) => {
        e.preventDefault();
        if (codeId != "") {
            setResendCode(true);
            const codeResult = await Api.resendAccountConfirmationCode(codeId);
            if(!codeResult.Error) {
                const newCodeId = codeResult.Data;
                if(newCodeId) {
                    setCodeId(newCodeId);
                    setShowCodeResendConfirmation(true);
                    setTimeout(() => {
                        setShowCodeResendConfirmation(false);
                    }, 5000);
                }
            }
            
            setConfirmationCode('');
            setResendCode(false);
        }
    };

    const renderCodeVerificationUI = () => {
        return (
                <>
                { didConfirmAccountDuringLogin?
                    <div className='form-item-title'>
                        <h3>&iexcl;Su cuenta se confirm&oacute; exitosamente!</h3>
                    </div> : <></>
                }
                <div className="form-item-title">
                    <h2>Entre su código de verificación</h2>
                </div>
                <div className='form-data-container'>
                    <div className="form-data-item">
                        <p>El c&oacute;digo ha sido enviado a su correo electr&oacute;nico.</p>
                    </div>
                </div>
                <form onSubmit={async (e) => await handleVerification(e)} className="form-data-container">
                    <div className="form-data-item">
                        <label htmlFor="confirmation">C&oacute;digo de Confirmaci&oacute;n</label>
                        <input
                            type="text"
                            id="confirmation"
                            value={confirmationCode}
                            onChange={(e) => setConfirmationCode(e.target.value)}
                            className="form-textbox"
                            placeholder="Entre su código"
                            inputMode='numeric'
                            maxLength={6}
                        />
                    </div>
                    <div className="form-data-item">
                        <a href='#' onClick={async (e) => await resendVerificationCode(e)}>
                            {resendCode ? (
                                <div className='spinner'></div>
                            ) : (
                                'Presiona aquí para enviar otro código'
                            )}
                        </a>
                        {
                            showCodeResendConfirmation ? <p>Su nuevo c&oacute;digo ha sido enviado a su correo electr&oacute;nico.</p> : <></>
                        }
                    </div>
                    {error && <div className="form-data-item" style={{ color: 'red' }}>{error}</div>}
                    <div className="form-data-item">
                        <button
                            type="submit"
                            className="form-button"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <div className='spinner'></div>
                            ) : (
                                'Confirmar'
                            )}
                        </button>
                    </div>
                </form>
            </>
        )
    }

    const renderAccountConfirmationUI = () => {
        return (
            <>
                <div className="form-item-title">
                    <h2>Confirme su Cuenta</h2>
                </div>
                <form onSubmit={async (e) => await handleConfirm(e)} className="form-data-container">
                    <div className="form-data-item">
                    <label htmlFor="confirmation">C&oacute;digo de Confirmaci&oacute;n</label>
                    <input
                        type="text"
                        id="confirmation"
                        value={confirmationCode}
                        onChange={(e) => setConfirmationCode(e.target.value)}
                        className="form-textbox"
                        placeholder="Entre su código"
                        inputMode='numeric'
                        maxLength={6}
                    />
                    </div>
                    <div className="form-data-item">
                        <a href='#' onClick={async (e) => await resendConfirmationCode(e)}>
                            {resendCode ? (
                            <div className='spinner'></div>
                            ) : (
                            'Presiona aquí para enviar otro código'
                            )}
                        </a>
                        {
                            showCodeResendConfirmation? <p>Su nuevo c&oacute;digo ha sido enviado a su correo electr&oacute;nico.</p> : <></>
                        }
                    </div>
                    {error && <div className="form-data-item" style={{ color: 'red' }}>{error}</div>}
                    <div className="form-data-item">
                    <button 
                        type="submit" 
                        className="form-button"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                        <div className='spinner'></div>
                        ) : (
                        'Confirmar'
                        )}
                    </button>
                    </div>
                </form>
            </>
        );
    }

    return (
        <>
            <NavBar />
            <div className="form-container">
                <div className="form-item">
                    {!isSubmitted ? (
                        <>
                            <div className="form-item-title">
                                <h2>Iniciar Sesi&oacute;n</h2>
                            </div>
                            <form onSubmit={async (e) => await handleSubmit(e)} className="form-data-container">
                                <div className="form-data-item">
                                    <label htmlFor="email">Correo Electr&oacute;nico</label>
                                    <input
                                        type="text"
                                        id="email"
                                        inputMode='email'
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="form-textbox"
                                        placeholder="juandelpueblo@ejemplo.com"
                                    />
                                </div>
                                {error && <div className="form-data-item" style={{ color: 'red' }}>{error}</div>}
                                <div className="form-data-item">
                                    <button
                                        type="submit"
                                        className="form-button"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <div className="spinner"></div>
                                        ) : (
                                            'Iniciar Sesión'
                                        )}
                                    </button>
                                </div>
                                <div className='form-data-item'>
                                    <p>¿Tiene una cuenta?</p>
                                    <Link to="/registrate">Presione aquí para registrarse.</Link>
                                </div>
                            </form>
                        </>
                    ) : (
                        <>
                            {
                                accountConfirmed ?
                                     renderCodeVerificationUI() : renderAccountConfirmationUI()
                            }
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default Login;
